import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, img, errMsg } from "@/lib/api";
import { fmtDate, money, TERMS } from "@/lib/format";
import { PageHead } from "@/components/portal/PortalShell";
import { Field, NativeSelect, StatusBadge, Empty } from "@/components/site/bits";
import { BranchFields, emptyBranch } from "@/pages/SellerRegister";

export function AdminMerchants() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/merchants", { params: { status: status || undefined, q: q || undefined } }).then((r) => setItems(r.data));
  }, [status, q]);
  return (
    <div>
      <PageHead title="Merchants" sub="Review registrations, activate packages and manage accounts." />
      <div className="flex flex-wrap gap-3 mb-4">
        <Input data-testid="merchant-search" placeholder="Search name, email, city" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <NativeSelect data-testid="merchant-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="!w-44"><option value="">All statuses</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="suspended">Suspended</option></NativeSelect>
      </div>
      {!items ? <div className="h-40 bg-slate-100 rounded-2xl animate-pulse" /> : items.length === 0 ? <Empty title="No merchants" /> : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="merchants-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Business</th><th className="p-3">City</th><th className="p-3">Status</th><th className="p-3">Package</th><th className="p-3">Expires</th><th className="p-3">Branches</th><th className="p-3"></th></tr></thead>
            <tbody>{items.map((m) => (
              <tr key={m.id} className="border-t border-slate-100">
                <td className="p-3"><b>{m.business_name}</b><br /><span className="text-xs text-slate-500">{m.email}</span></td><td className="p-3">{m.city}</td>
                <td className="p-3"><StatusBadge status={m.status} /></td><td className="p-3">{m.subscription ? `${m.subscription.package_name} (${m.subscription.slots})` : "—"}</td>
                <td className="p-3">{m.subscription ? fmtDate(m.subscription.expires_at, "Asia/Kolkata", false) : "—"}</td><td className="p-3">{m.branches_count}</td>
                <td className="p-3"><Link data-testid={`manage-merchant-${m.id}`} to={`/admin/merchants/${m.id}`} className="btn-act">Manage</Link></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const PAY = { pending: "Pending", paid_offline: "Paid offline", complimentary: "Complimentary", waived: "Waived" };

function ActivateDialog({ merchantId, packages, preferred, onClose, onDone }) {
  const [f, setF] = useState({ package_id: preferred || "", term: "initial", start_at: new Date().toISOString().slice(0, 10), custom_months: "", custom_days: "", payment_status: "pending", payment_method: "offline", payment_reference: "", notes: "", complimentary_reason: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pkg = packages.find((p) => p.id === f.package_id);
  const extra = { initial: 0, initial_monthly: pkg?.monthly_price, initial_six: pkg?.six_month_price, initial_annual: pkg?.annual_price }[f.term] || 0;
  const submit = async () => {
    try {
      await api.post(`/admin/merchants/${merchantId}/subscriptions/activate`, { ...f, start_at: new Date(f.start_at).toISOString(), custom_months: f.custom_months ? +f.custom_months : null, custom_days: f.custom_days ? +f.custom_days : null });
      toast.success("Package activated");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" data-testid="activate-dialog">
        <DialogHeader><DialogTitle className="font-heading">Activate package</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Package"><NativeSelect data-testid="activate-package-select" value={f.package_id} onChange={set("package_id")}><option value="">Select…</option>{packages.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.slots} deals</option>)}</NativeSelect></Field>
          <Field label="Validity"><NativeSelect data-testid="activate-term-select" value={f.term} onChange={set("term")}>{["initial", "initial_monthly", "initial_six", "initial_annual", "custom"].map((t) => <option key={t} value={t}>{TERMS[t]}</option>)}</NativeSelect></Field>
          {f.term === "custom" && <div className="grid grid-cols-2 gap-2"><Field label="Months"><Input data-testid="activate-custom-months" type="number" value={f.custom_months} onChange={set("custom_months")} /></Field><Field label="Days"><Input data-testid="activate-custom-days" type="number" value={f.custom_days} onChange={set("custom_days")} /></Field></div>}
          <Field label="Activation date"><Input data-testid="activate-start-input" type="date" value={f.start_at} onChange={set("start_at")} /></Field>
          <Field label="Payment status" hint="Activation does not imply payment was received."><NativeSelect data-testid="activate-payment-select" value={f.payment_status} onChange={set("payment_status")}>{Object.entries(PAY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect></Field>
          {["complimentary", "waived"].includes(f.payment_status) && <Field label="Documented reason (required)"><Input data-testid="activate-reason-input" value={f.complimentary_reason} onChange={set("complimentary_reason")} /></Field>}
          <Field label="Payment reference"><Input data-testid="activate-reference-input" value={f.payment_reference} onChange={set("payment_reference")} /></Field>
          <Field label="Internal notes"><Textarea data-testid="activate-notes-input" value={f.notes} onChange={set("notes")} /></Field>
          {pkg && f.term !== "custom" && <p className="text-sm font-semibold" data-testid="activate-price">Amount: {money(pkg.activation_price + extra)}</p>}
          <button data-testid="activate-submit" disabled={!f.package_id} onClick={submit} className="w-full h-11 rounded-xl bg-red-500 disabled:bg-slate-300 text-white font-bold">Activate package</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RenewDialog({ sub, onClose, onDone }) {
  const [f, setF] = useState({ term: "monthly", payment_status: "pending", payment_reference: "", notes: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    try {
      await api.post(`/admin/subscriptions/${sub.id}/renew`, f);
      toast.success("Subscription renewed");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="renew-dialog">
        <DialogHeader><DialogTitle className="font-heading">Renew {sub.package_name}</DialogTitle></DialogHeader>
        <NativeSelect data-testid="renew-term-select" value={f.term} onChange={set("term")}>{["monthly", "six", "annual"].map((t) => <option key={t} value={t}>{TERMS[t]}</option>)}</NativeSelect>
        <NativeSelect data-testid="renew-payment-select" value={f.payment_status} onChange={set("payment_status")}>{Object.entries(PAY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect>
        <Input placeholder="Payment reference" value={f.payment_reference} onChange={set("payment_reference")} />
        <Textarea placeholder="Notes" value={f.notes} onChange={set("notes")} />
        <button data-testid="renew-submit" onClick={submit} className="h-11 rounded-xl bg-red-500 text-white font-bold">Renew</button>
      </DialogContent>
    </Dialog>
  );
}

export function AdminMerchantDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [packages, setPackages] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [branch, setBranch] = useState(null);
  const load = () => api.get(`/admin/merchants/${id}`).then((r) => setD(r.data));
  useEffect(() => {
    load();
    api.get("/admin/packages").then((r) => setPackages(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const post = async (url, msg, body = {}) => {
    try {
      await api.post(url, body);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const subAct = (sid, action) => {
    const reason = window.prompt(`Reason to ${action} this subscription?`) ?? null;
    if (reason !== null) post(`/admin/subscriptions/${sid}/${action}`, `Subscription ${action}d`, { reason });
  };
  const saveBranch = async () => {
    try {
      await api.post(`/admin/merchants/${id}/branches`, { ...branch, lat: +branch.lat, lng: +branch.lng });
      toast.success("Branch added");
      setBranch(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!d) return <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" />;
  const m = d.merchant;
  const cur = d.current_subscription;
  return (
    <div className="space-y-6">
      <PageHead title={m.business_name} sub={`${m.city} · ${m.email} · ${m.phone}`}>
        <StatusBadge status={m.status} testid="admin-merchant-status" />
        {m.status !== "approved" && <button data-testid="approve-merchant" onClick={() => post(`/admin/merchants/${id}/approve`, "Merchant approved")} className="btn-act bg-emerald-600 text-white">Approve</button>}
        {m.status !== "suspended" && <button data-testid="suspend-merchant" onClick={() => post(`/admin/merchants/${id}/suspend`, "Merchant suspended", { reason: window.prompt("Reason?") || "" })} className="btn-act">Suspend</button>}
        <button data-testid="feature-merchant" onClick={() => post(`/admin/merchants/${id}/featured`, "Featured toggled")} className="btn-act">{m.featured ? "Unfeature" : "Feature"}</button>
        <button data-testid="open-activate" onClick={() => setDlg("activate")} className="btn-act bg-red-500 text-white">Activate package</button>
      </PageHead>
      {d.requests.filter((r) => r.status === "open").map((r) => (
        <div key={r.id} data-testid="admin-open-request" className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">Activation request: <b>{r.package_name}</b> · {TERMS[r.term]} · {fmtDate(r.created_at)} {r.note && `— “${r.note}”`}</div>
      ))}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-2 text-sm">
          <h2 className="font-heading font-bold">Business</h2>
          {m.logo && <img src={img(m.logo)} alt="" className="w-16 h-16 rounded-xl object-cover" />}
          <p>Contact: <b>{m.contact_name}</b></p><p>GSTIN: {m.gstin || "—"}</p><p>{m.address}, {m.city} {m.postal_code}</p>
          <p>Preferred package: <b>{d.preferred_package?.name || "—"}</b></p><p>Deals created: {d.deals_count}</p><p className="text-slate-500">{m.description}</p>
        </div>
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="admin-current-sub">
          <h2 className="font-heading font-bold">Current subscription</h2>
          {cur ? (<>
            <div className="grid sm:grid-cols-4 gap-3 text-sm"><p>Package<br /><b>{cur.package_name}</b></p><p>Slots used<br /><b>{d.slots_used} / {cur.slots}</b></p><p>Valid until<br /><b>{fmtDate(cur.expires_at)}</b></p><p>Payment<br /><StatusBadge status={cur.payment_status} /></p></div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="renew-sub" onClick={() => setDlg("renew")} className="btn-act">Renew</button>
              <button data-testid="suspend-sub" onClick={() => subAct(cur.id, "suspend")} className="btn-act">Suspend</button>
              <button data-testid="expire-sub" onClick={() => subAct(cur.id, "expire")} className="btn-act text-red-600">Expire now</button>
            </div>
          </>) : <p className="text-sm text-slate-500">No active subscription.</p>}
          <div className="text-sm">
            {d.subscriptions.map((s) => (
              <div key={s.id} className="flex flex-wrap justify-between gap-2 py-2 border-t border-slate-100">
                <span><StatusBadge status={s.status} /> <b>{s.package_name}</b> · {TERMS[s.subscription_type] || s.subscription_type} · {money(s.price)}</span>
                <span className="text-slate-500">{fmtDate(s.start_at, "Asia/Kolkata", false)} → {fmtDate(s.expires_at, "Asia/Kolkata", false)} {s.status === "suspended" && <button data-testid={`resume-sub-${s.id}`} onClick={() => subAct(s.id, "resume")} className="btn-act ml-2">Resume</button>}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex justify-between mb-2"><h2 className="font-heading font-bold">Branches</h2><button data-testid="admin-add-branch" onClick={() => setBranch({ ...emptyBranch })} className="btn-act">+ Add branch</button></div>
        {d.branches.map((b) => <div key={b.id} className="py-2 border-t border-slate-100 text-sm"><b>{b.name}</b> · {b.address}, {b.city} <span className="text-slate-400">({b.lat}, {b.lng})</span></div>)}
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-heading font-bold mb-2">Activation history</h2>
        {d.events.map((e) => <div key={e.id} className="flex flex-wrap justify-between py-2 border-t border-slate-100 text-sm"><span><StatusBadge status={e.type} /> {e.months ? `${e.months} month(s)` : ""} {e.amount ? money(e.amount) : ""} {e.payment_status && <StatusBadge status={e.payment_status} />} {e.payment_reference && `Ref: ${e.payment_reference}`} {e.notes && `— ${e.notes}`}</span><span className="text-slate-500">{e.by_email} · {fmtDate(e.at)}</span></div>)}
      </div>
      {dlg === "activate" && <ActivateDialog merchantId={id} packages={packages} preferred={m.preferred_package_id} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
      {dlg === "renew" && cur && <RenewDialog sub={cur} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
      <Dialog open={!!branch} onOpenChange={() => setBranch(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto"><DialogHeader><DialogTitle>Add branch</DialogTitle></DialogHeader>{branch && <BranchFields b={branch} setB={setBranch} prefix="admin-branch" />}<button data-testid="admin-branch-save" onClick={saveBranch} className="h-11 rounded-xl bg-red-500 text-white font-bold">Save</button></DialogContent>
      </Dialog>
    </div>
  );
}
